import { memo, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import '../../assets/css/AGGrid-custom.css';
import { Box, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { CustomButton } from '../../components/common/customButton';
import { renderSubTitle } from '../../components/renderComponents';
import { useDealProvider } from '../../context/DealContext';
import { apiGet } from '../../services/genesisApi/httpsMethods';
import type { Contact } from '../../types/deal';

ModuleRegistry.registerModules([AllCommunityModule]);

type ExtractedContactRow = Record<string, any> & {
  id: number | string;
  contact_name?: string;
  company?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  contact_title?: string | null;
  contact_location?: string | null;
  notes?: string | null;
  // UI-only flags
  shouldCreate?: boolean;
  shouldLink?: boolean;
};

const normalizeEmail = (email: any) =>
  typeof email === 'string' ? email.trim().toLowerCase() : '';

const ExtractedContactsTable = ({
  dealId,
  allContacts,
}: {
  dealId: number | null;
  allContacts?: Contact[];
}) => {
  const { contacts, setContacts } = useDealProvider();
  const gridRef = useRef<any>(null);
  const isEditDealContext = !!dealId;
  const lastDealIdRef = useRef<number | null>(null);

  const existingByEmail = useMemo(() => {
    const map = new Map<string, Contact>();
    (allContacts || []).forEach((c) => {
      const key = normalizeEmail((c as any)?.contact_email);
      if (key) map.set(key, c);
    });
    return map;
  }, [allContacts]);

  useEffect(() => {
    // In CREATE mode (no dealId), contacts are driven by extraction + user edits.
    // Do NOT clear them when reference lists load, or they will "flash then disappear".
    if (!dealId) return;

    // Prefer the already-loaded reference list (avoids a redundant fetch).
    if (Array.isArray(allContacts) && allContacts.length > 0) {
      const linked = allContacts.filter((c: any) =>
        Array.isArray(c?.links) && c.links.some((l: any) => l?.deal_id === dealId)
      );
      const withFlags: ExtractedContactRow[] = linked.map((c: any, index: number) => ({
        ...c,
        id: c.id || `temp-contact-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 9)}`,
        shouldLink: true,
      }));
      // Merge into existing contacts (e.g. extraction-populated state) to avoid overwriting due to race conditions.
      const mergeContacts = (prev: ExtractedContactRow[], incoming: ExtractedContactRow[]) => {
        const merged = [...(prev || [])];
        incoming.forEach((inc) => {
          const email = normalizeEmail(inc.contact_email);
          const existingIndex = merged.findIndex((c) => {
            if (typeof inc?.id === 'number' && typeof c?.id === 'number') return inc.id === c.id;
            const existingEmail = normalizeEmail(c.contact_email);
            return email && existingEmail ? email === existingEmail : false;
          });
          if (existingIndex >= 0) {
            merged[existingIndex] = { ...merged[existingIndex], ...inc, shouldLink: true };
          } else {
            merged.push(inc);
          }
        });
        return merged;
      };

      setContacts((prev) => {
        const prevList = (prev as ExtractedContactRow[]) || [];
        const isNewDealContext = lastDealIdRef.current !== dealId && prevList.length === 0;
        lastDealIdRef.current = dealId;
        return (isNewDealContext ? withFlags : mergeContacts(prevList, withFlags)) as any;
      });
      setTimeout(() => {
        gridRef.current?.api.refreshCells({ force: true });
      }, 100);
      return;
    }

    apiGet('contacts')
      .then((response) => {
        if (response && response.success && Array.isArray(response.data)) {
          const linked = response.data.filter((c: any) =>
            Array.isArray(c?.links) && c.links.some((l: any) => l?.deal_id === dealId)
          );
          const withFlags: ExtractedContactRow[] = linked.map((c: any, index: number) => ({
            ...c,
            id: c.id || `temp-contact-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 9)}`,
            shouldLink: true,
          }));
          setContacts((prev) => {
            const prevList = (prev as ExtractedContactRow[]) || [];
            const isNewDealContext = lastDealIdRef.current !== dealId && prevList.length === 0;
            lastDealIdRef.current = dealId;
            return (isNewDealContext ? withFlags : [...prevList, ...withFlags]) as any;
          });
          setTimeout(() => {
            gridRef.current?.api.refreshCells({ force: true });
          }, 100);
        }
      })
      .catch((err) => console.error('Failed to fetch contacts', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, allContacts]);

  const handleCheckboxChange = (checked: boolean, rowId: number | string) => {
    setContacts((prev) => {
      const next = (prev as ExtractedContactRow[]).map((c) => {
        if (c.id === rowId) {
          return isEditDealContext ? { ...c, shouldLink: checked } : { ...c, shouldCreate: checked };
        }
        return c;
      });
      return next as any;
    });
    gridRef.current?.api.refreshCells({ force: true, suppressFlash: true });
  };

  const handleDeleteRowById = (rowId: number | string) => {
    if (rowId === null || rowId === undefined) return;
    setContacts((prev) => (prev as ExtractedContactRow[]).filter((c) => c.id !== rowId) as any);
  };

  const columnDefs: ColDef[] = useMemo(() => {
    return [
      {
        headerName: '',
        field: 'included',
        width: 50,
        editable: false,
        cellRenderer: (params: ICellRendererParams) => {
          const isChecked = isEditDealContext
            ? params.data?.shouldLink !== false
            : params.data?.shouldCreate !== false;

          return (
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => {
                e.stopPropagation();
                handleCheckboxChange(e.target.checked, params.data?.id);
              }}
            />
          );
        },
        onCellClicked: (params: any) => {
          if (params.column.getColId() !== 'included') return;
          const currentChecked = isEditDealContext
            ? params.data?.shouldLink !== false
            : params.data?.shouldCreate !== false;
          handleCheckboxChange(!currentChecked, params.data?.id);
        },
      },
      {
        headerName: '',
        field: 'delete',
        width: 50,
        editable: false,
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const rowId = params.data?.id as number | string | undefined;
                if (rowId !== null && rowId !== undefined) {
                  handleDeleteRowById(rowId);
                }
              }}
              sx={{ padding: '2px' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          );
        },
      },
      {
        headerName: 'Existing?',
        colId: 'is_existing',
        editable: false,
        width: 95,
        valueGetter: (params: any) => {
          // If row already has a DB id, it's existing.
          if (typeof params?.data?.id === 'number') return 'Existing';
          const email = normalizeEmail(params?.data?.contact_email);
          return email && existingByEmail.has(email) ? 'Existing' : 'New';
        },
        cellStyle: (params: any) => {
          const value = params?.value;
          const isExisting = value === 'Existing';
          return {
            backgroundColor: isExisting ? '#fff8e1' : '#e8f5e9', // amber / green
            color: '#1f1f1f',
            textAlign: 'center',
          };
        },
      },
      { field: 'contact_name', headerName: 'Contact', editable: true, width: 160 },
      { field: 'company', headerName: 'Company', editable: true, width: 140 },
      { field: 'contact_email', headerName: 'Email', editable: true, width: 220 },
      { field: 'contact_phone', headerName: 'Phone', editable: true, width: 140 },
      { field: 'contact_title', headerName: 'Title', editable: true, width: 180 },
      { field: 'contact_location', headerName: 'Location', editable: true, width: 140 },
      { field: 'notes', headerName: 'Notes', editable: true, width: 280 },
    ];
  }, [isEditDealContext, existingByEmail]);

  const onCellValueChanged = (event: any) => {
    const { colDef, newValue, data } = event;
    const field = colDef.field;
    const rowId = data?.id as number | string | undefined;
    if (rowId === null || rowId === undefined) return;

    setContacts((prev) => {
      const next = (prev as ExtractedContactRow[]).map((c) => (c.id === rowId ? { ...c, [field]: newValue } : c));
      return next as any;
    });
  };

  const handleAddRow = () => {
    const newRow: ExtractedContactRow = {
      id: `new-contact-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      contact_name: '',
      company: '',
      contact_email: '',
      contact_phone: '',
      contact_title: '',
      contact_location: '',
      notes: '',
      ...(isEditDealContext ? { shouldLink: true } : { shouldCreate: true }),
    };
    setContacts((prev) => ([...(prev as ExtractedContactRow[]), newRow] as any));
  };

  return (
    <>
      {renderSubTitle('Contacts')}

      <div className="ag-theme-alpine" style={{ height: 280, width: '100%', marginTop: 10 }}>
        <AgGridReact
          ref={gridRef}          rowData={contacts as any}
          columnDefs={columnDefs}
          onCellValueChanged={onCellValueChanged}
          singleClickEdit={true}
          stopEditingWhenCellsLoseFocus={true}
          defaultColDef={{ flex: 0, resizable: true }}
          rowHeight={30}
          getRowId={({ data }) => data?.id?.toString() || `fallback-${Math.random().toString(36).slice(2, 9)}`}
        />
      </div>

      <Box display="flex" gap={2} mb={1} mt={2}>
        <CustomButton handleButtonPress={handleAddRow} isButtonDisabled={false} solid={false} text="Add Row" />
      </Box>
    </>
  );
};

// Memoized so parent forms (e.g. CreateDealUpdate) can re-render on each
// keystroke without re-rendering this component.
export default memo(ExtractedContactsTable);


